import { Link } from "@tanstack/react-router";
import type { ContentRef, ContentType } from "@/lib/related";

const TYPE_LABEL: Record<ContentType, string> = {
  service: "Service",
  blog: "Article",
  glossary: "Glossary",
  template: "Template",
  calculator: "Calculator",
  industry: "Industry",
};

function linkProps(item: ContentRef) {
  switch (item.type) {
    case "service":
      return { to: "/services/$slug" as const, params: { slug: item.slug } };
    case "industry":
      return { to: "/hr-by-industry/$slug" as const, params: { slug: item.slug } };
    case "blog":
      return { to: "/hr-insights/$slug" as const, params: { slug: item.slug } };
    case "glossary":
      return { to: "/hr-glossary/$slug" as const, params: { slug: item.slug } };
    case "template":
      return { to: "/hr-templates/$slug" as const, params: { slug: item.slug } };
    case "calculator":
      return { to: "/hr-calculators/$slug" as const, params: { slug: item.slug } };
  }
}

interface RelatedContentProps {
  /** Heading anchor, should include the page's primary keyword for SEO. */
  heading: string;
  items: ContentRef[];
  className?: string;
}

/**
 * Mixed-type related-content rail. Renders nothing if no items.
 * Anchor text intentionally uses each target's own title (descriptive
 * anchor text, not "click here").
 */
export function RelatedContent({ heading, items, className }: RelatedContentProps) {
  if (items.length === 0) return null;
  return (
    <section className={className ?? "px-6 py-16 lg:px-8"}>
      <div className="mx-auto max-w-6xl">
        <h2 className="font-serif text-2xl text-brand sm:text-3xl">{heading}</h2>
        <ul className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item) => (
            <li key={`${item.type}-${item.slug}`}>
              <Link
                {...linkProps(item)}
                className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 transition hover:border-brand hover:shadow-sm"
              >
                <p className="text-[10px] font-semibold uppercase tracking-widest text-accent">
                  {TYPE_LABEL[item.type]}
                </p>
                <p className="mt-3 font-serif text-lg leading-snug text-brand group-hover:text-accent">
                  {item.title}
                </p>
                <p className="mt-2 line-clamp-3 text-sm text-muted-foreground">{item.summary}</p>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
